import { supabase, supabaseEndpoint } from '../client';

/**
 * Client wrapper around the `shop-notify` Edge Function.
 *
 * Mirrors the `sendEmail` pattern: direct `fetch` (not
 * `supabase.functions.invoke`) for stable CORS and unmasked error
 * surfaces, never throws, always returns `{ ok, error? }` so that
 * transactional callers (state-transition RPCs in shopPurchaseOrders)
 * can surface a notification failure without rolling back the state
 * change that was the actual user action.
 *
 * Authorization: forwards the caller's JWT. The function re-checks
 * access against `shop.purchase_orders` / `shop.orders` under the
 * user's RLS context, so any caller who could legitimately read the
 * row (the buyer, or a member of the vendor's team) can ask for the
 * notification. `shop-shippo-track` invokes the same function with
 * `x-pc-internal-secret` instead.
 */

export type ShopNotifyEvent =
  | 'po_submitted'
  | 'po_denied'
  | 'po_revised'
  | 'po_accepted'
  | 'order_delivered'
  // Phase B -- order_issues lifecycle. `refId` is shop.order_issues.id
  // for all three. issue_opened ALSO sends a CC to the platform support
  // mailbox so we can spot vendor performance issues early.
  | 'issue_opened'
  | 'issue_vendor_responded'
  | 'issue_resolved';

export interface NotifyShopEventInput {
  event: ShopNotifyEvent;
  refId: string;
  vendorNote?: string;
}

export interface NotifyShopEventResult {
  ok: boolean;
  sent?: string[];
  skipped?: string[];
  error?: string;
}

export async function notifyShopEvent(
  input: NotifyShopEventInput,
): Promise<NotifyShopEventResult> {
  const sb = supabase();
  const { data: sess } = await sb.auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) {
    return {
      ok: false,
      error: 'Your session has expired. Please sign in again and retry.',
    };
  }

  const { url: baseUrl, anonKey } = supabaseEndpoint();
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/shop-notify`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event: input.event,
        ref_id: input.refId,
        vendor_note: input.vendorNote,
      }),
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : 'Error';
    const msg = err instanceof Error ? err.message : String(err);
    return {
      ok: false,
      error: `Network failure invoking shop-notify (${name}): ${msg}`,
    };
  }

  let body: NotifyShopEventResult | null = null;
  let raw: string | null = null;
  try {
    raw = await resp.text();
    if (raw) body = JSON.parse(raw) as NotifyShopEventResult;
  } catch {
    // body wasn't JSON
  }

  if (!resp.ok) {
    const detail = body?.error ?? raw ?? `HTTP ${resp.status}`;
    return { ok: false, error: detail };
  }

  return body ?? { ok: false, error: 'shop-notify returned an empty body.' };
}
